// ---------------------------------------------------------------------------
// resolveEquippedThemeId — fonte única do tema QUE APARECE, para as duas
// superfícies que renderizam o tema de alguém: o app do dono (ThemeContext) e
// o perfil público visto por outra pessoa (usePublicProfileState).
//
// Antes a regra existia duplicada e DIVERGENTE:
//
//   · ThemeContext lia `themePreference` e aplicava três guards (alias legado,
//     PRO, coin-gate).
//   · O perfil público lia `themeId` cru, sem guard nenhum.
//
// `themeId` é escrito em exatamente dois lugares — criação da conta
// (`"default"`) e fim do onboarding. Os dois caminhos de equipar tema
// (`ThemePreviewBar`, `CosmeticPicker`) só escrevem `themePreference`. Ou
// seja: toda troca de tema feita DEPOIS do onboarding ficava invisível para
// quem visitava o perfil — o dono via o tema novo no aparelho dele, o
// visitante via o do onboarding para sempre.
//
// A ordem de leitura aqui (`themePreference` primeiro, `themeId` como
// fallback) conserta os perfis já gravados sem migração de dados: o campo
// canônico é o que o app escreve de verdade. O espelho `themeId` continua
// existindo (agora mantido em dia por `updateUserProfile`) para docs antigos
// e para leitores que só tenham ele.
//
// Os guards são os mesmos nos dois lados de propósito: se o tema premium some
// pro dono que perdeu o PRO, ele tem que sumir pro visitante também — senão o
// perfil público anuncia um tema que o próprio dono não está vendo.
// ---------------------------------------------------------------------------

import {
  COIN_GATED_THEME_IDS,
  FREE_THEME_IDS,
  THEME_BADGE_REQUIREMENT,
  THEME_EVENT_OF,
  THEMES,
} from "../../config/themes";
import { isThemeEventOpen } from "../../config/themeEvents";

/**
 * Temas exclusivos de Founder. Derivado do catálogo, não escrito à mão: lista
 * paralela é como `themeId` e `themePreference` divergiram (BUG-80).
 */
const FOUNDER_THEME_IDS: readonly string[] = THEMES.filter((t) => t.isFounder === true).map(
  (t) => t.id,
);

/**
 * Temas aposentados que continuam gravados em `users/{uid}`. Sem o mapa, quem
 * tinha o tema salvo cai no `default` e "perde" a escolha sem entender por quê.
 *   · `custom` → `noir` (renomeado quando o custom virou gratuito)
 *   · `blush` → `cerejeira` (Blush Romance aposentado em 26/07)
 */
const THEME_ALIASES: Record<string, string> = {
  custom: "noir",
  blush: "cerejeira",
};

export interface EquippedThemeSource {
  /** Canônico — o que os caminhos de equipar escrevem. */
  themePreference?: string | null;
  /** Espelho público (docs antigos podem ter só ele, ou ter ele defasado). */
  themeId?: string | null;
  /** Temas comprados com moedas (client-locked). */
  unlockedThemes?: readonly string[] | null;
  /** Assinatura ativa do DONO do tema — não de quem está olhando. */
  isPro?: boolean | null;
  /**
   * Dono é Founder (`users.legacyTags` contém "founder"). Separado de `isPro`
   * porque Founder É PRO, mas PRO não é Founder: sem o campo próprio, o guard
   * de assinatura deixaria qualquer assinante equipar o tema do fundador.
   */
  isFounder?: boolean | null;
  /**
   * Badges do DONO do tema (`users.badges`, client-locked por exclusão da
   * whitelist). É daqui que sai o direito ao tema conquistado — e não do
   * inventário, por uma razão medida: `user_inventory` é `read: if isOwner`,
   * então o visitante do perfil público não consegue lê-lo. Guard que só o
   * dono consegue avaliar é guard que faz dono e visitante verem temas
   * diferentes, que é literalmente o BUG-80.
   */
  badges?: readonly string[] | null;
  /**
   * Ids dos temas criados no PAINEL e publicados (`theme_configs` com
   * `source: "admin"`). Eles não existem no catálogo do código.
   *
   * ⚠️ **As duas superfícies têm de passar a MESMA lista.** O dono
   * (`ThemeContext`) e o visitante do perfil público (`usePublicProfileState`)
   * chegam aqui pelo mesmo resolvedor de propósito: se uma delas conhecer os
   * temas do painel e a outra não, o dono vê um tema que o perfil público não
   * mostra — que é exatamente o BUG-80, agora com item pago.
   *
   * Ausente é seguro (o tema cai em `default` nos dois lados), mas ausente numa
   * ponta só, não.
   */
  adminThemeIds?: readonly string[] | null;
  /**
   * O tema que a pessoa usava ANTES de equipar um tema de evento
   * (`users.themeBeforeEvent`, gravado junto do `themePreference` pelo
   * `themeEquipUpdate`). É para onde ela volta quando o evento fecha.
   *
   * ⚠️ Lido nas TRÊS superfícies (dono, perfil público, `/lendo`) e no
   * servidor (cartão de Leitores parecidos). Uma superfície que não o passe
   * devolveria `default` onde as outras devolvem o tema de antes — BUG-80.
   */
  themeBeforeEvent?: string | null;
  /** Relógio injetável para teste. Ausente = agora. */
  at?: Date;
}

/** Aplica o mapa de temas aposentados. Vazio/ausente vira `default`. */
export function normalizeThemeAlias(raw: string | null | undefined): string {
  const id = typeof raw === "string" ? raw.trim() : "";
  if (!id) return "default";
  return THEME_ALIASES[id] ?? id;
}

/**
 * Tema efetivamente exibido, na ordem: alias legado → guard FOUNDER → guard
 * PRO → guard de coin-gate → guard de BADGE. Tema desconhecido/não permitido
 * sempre cai em `default` — nunca lança.
 */
export function resolveEquippedThemeId(source: EquippedThemeSource): string {
  const rawPref =
    typeof source.themePreference === "string" && source.themePreference.trim()
      ? source.themePreference
      : source.themeId;
  const pref = normalizeThemeAlias(rawPref);

  // Guard de EVENTO — o único em que a data decide o direito (ver
  // `config/themeEvents.ts`). Fora da janela a pessoa volta ao tema de antes,
  // que passa por TODOS os guards abaixo como qualquer preferência: o tema de
  // antes pode ter deixado de ser dela (perdeu o PRO no meio do evento).
  //
  // ⚠️ Profundidade 1, de propósito: o tema de antes nunca é de evento (o
  // gravador não grava; se o doc vier sujo, cai no `default`) — senão dois
  // eventos encadeados podiam se apontar um para o outro.
  const eventId = THEME_EVENT_OF[pref];
  if (eventId && !isThemeEventOpen(eventId, source.at)) {
    const before = normalizeThemeAlias(source.themeBeforeEvent);
    if (THEME_EVENT_OF[before]) return "default";
    return resolveEquippedThemeId({
      ...source,
      themePreference: before,
      themeId: null,
      themeBeforeEvent: null,
    });
  }

  // Founder guard — ANTES do de PRO, e não depois: Founder implica `isPro`
  // true, então o guard de assinatura sozinho aprovaria o tema para qualquer
  // assinante. Este é o único guard que a passagem por PRO não cobre.
  if (FOUNDER_THEME_IDS.includes(pref) && !source.isFounder) return "default";

  // Tema criado no PAINEL — provenance própria, regra completa, saída imediata.
  //
  // Ele não está em nenhuma das listas do código (`FREE_THEME_IDS`,
  // `COIN_GATED_THEME_IDS`), então cairia no guard de PRO e viraria `default`
  // para toda conta grátis — inclusive a que pagou por ele.
  //
  // A regra é a mesma dos coin-gated, porque tema do painel É coin-gated por
  // construção (`adminSetThemeConfig` não deixa escolher tier): o direito é
  // `unlockedThemes`, e nada mais.
  //
  // ⚠️ `adminThemeIds` AUSENTE faz o tema cair no guard de PRO e virar
  // `default`. Isso é falha FECHADA e, mais importante, **simétrica**: dono e
  // visitante que não conseguirem ler a lista chegam os dois em `default`. Uma
  // falha assimétrica aqui é literalmente o BUG-80 — o dono vendo um tema que o
  // perfil público não mostra.
  if (source.adminThemeIds?.includes(pref)) {
    const unlocked = source.unlockedThemes ?? [];
    return unlocked.includes(pref) ? pref : "default";
  }

  // PRO guard — assinatura necessária
  if (!source.isPro && !FREE_THEME_IDS.includes(pref)) return "default";

  // Coin-gate guard — tema requer compra com moedas
  if ((COIN_GATED_THEME_IDS as readonly string[]).includes(pref)) {
    const unlocked = source.unlockedThemes ?? [];
    if (!unlocked.includes(pref)) return "default";
  }

  // Badge guard — tema conquistado. Fica DEPOIS do de PRO de propósito, e o
  // tema segue dentro de `FREE_THEME_IDS`: excluí-lo de lá faria o guard de
  // assinatura matar toda conta grátis que ganhou a badge, ou seja o item
  // conquistado viraria exclusivo de assinante. Quem barra é este bloco.
  const requiredBadge = THEME_BADGE_REQUIREMENT[pref];
  if (requiredBadge) {
    const badges = source.badges ?? [];
    if (!badges.includes(requiredBadge)) return "default";
  }

  return pref;
}

/**
 * Todos os temas que a pessoa PODE equipar, na ordem do catálogo.
 *
 * Derivado de `resolveEquippedThemeId`, e não de uma lista paralela de guards:
 * "tema que eu tenho" é exatamente "tema que, se eu pedir, o resolvedor devolve
 * em vez de me jogar no `default`". Escrever os três guards de novo aqui é o
 * mesmo erro que fez `themeId` e `themePreference` divergirem (BUG-80) — a
 * cópia acerta no dia em que nasce e envelhece sozinha.
 *
 * ⚠️ `default` entra sempre. Ele é o valor de queda do resolvedor, então a
 * checagem `resolve(id) === id` o aprovaria mesmo se algum dia ele deixasse de
 * ser gratuito — e um catálogo sem nenhuma opção seria pior que um com uma.
 */
export function listOwnedThemeIds(
  source: Omit<EquippedThemeSource, "themePreference" | "themeId">,
): string[] {
  const doCodigo = THEMES.filter(
    (t) => t.id === "default" || resolveEquippedThemeId({ ...source, themePreference: t.id }) === t.id,
  ).map((t) => t.id);

  // Os do painel entram pela MESMA régua ("o resolvedor devolve o que eu pedi?")
  // em vez de uma segunda checagem de posse. Sem esta linha, quem comprasse um
  // tema criado no admin não conseguiria equipá-lo na Aparência: ele estaria em
  // `unlockedThemes`, o resolvedor o aceitaria, e a lista simplesmente não o
  // ofereceria — a classe "comprei e sumiu".
  const doPainel = (source.adminThemeIds ?? []).filter(
    (id) => resolveEquippedThemeId({ ...source, themePreference: id }) === id,
  );

  return [...doCodigo, ...doPainel];
}
